import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {connect} from 'react-redux';
import {ExclamationCircleFilled} from '@ant-design/icons';
import {Transition} from 'react-transition-group';
import {StyledJournalEntriesTabs, StyledJournalEntriesTabsContainer} from './StyledJournalEntriesTabs';
import ReloadButton from '../ReloadButton';
import EditAccountingRowSection from '../EditAccountingRowSection';
import TableFiltersRightSideContent from '../TableFiltersRightSideContent';
import JournalEntryReviewTab from '../tabs/JournalEntryReviewTab';
import MissingBillsTab from '../tabs/MissingBillsTab';
import JournalEntryHistoryTab from '../tabs/JournalEntryHistoryTab';
import {
  accountingEditVariants,
  DEFAULT_TABLE_DATA,
  journalEntryStatusesConstants,
  journalEntrySyncStatusConstants,
  SYSTEM_ANIMATION_DURATION
} from '../../../../constants';
import {useAccountingTabLogic, useTabState} from '../../../../hooks';
import {
  accountingHelpers,
  elementHelpers,
  invoicesHelpers,
  loadHelpers,
  locationHelpers,
  tabHelpers,
  transactionsHelpers,
  vendorsHelpers,
} from '../../../../utils/helpers';
import {accountingActions} from '../../../../state/actions/accounting.actions';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';

const tabKeys = {
  MISSING_BILLS: 'missing_bills',
  NOT_ON_ERP: 'not_on_erp',
  READY_TO_SYNC: 'ready_to_sync',
  SYNCED: 'synced',
};

const {MISSING_BILLS, NOT_ON_ERP, READY_TO_SYNC, SYNCED} = tabKeys;

const defaultTabKey = tabKeys.MISSING_BILLS;

const tabKeysArr = [MISSING_BILLS, NOT_ON_ERP, READY_TO_SYNC, SYNCED];

const {showAlert} = accountingHelpers;

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION, detailsMaxWidth: 510});

const JournalEntriesTabs = ({
  dispatch,
  getBatchExpenses,
  getBatchInvoices,
  getJournalEntries,
  getJournalEntriesTotals,
  readyToSyncJournalEntries,
  resetJournalEntry,
  syncJournalEntries,
  updateJournalEntry,
  vendors,
  ...rest
}) => {
  const location = useLocation();
  const [t] = useTranslation(['main', 'accounting']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || defaultTabKey);
  const [expensesMap, setExpensesMap] = useState({});
  const [invoicesMap, setInvoicesMap] = useState({});
  const [total, setTotal] = useState(null);
  const [detailsWindowProps, setDetailsWindowProps] = useState({open: false, item: null, variant: null});
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
    requiredUpdate, setRequiredUpdate,
    selectedRowKeys, setSelectedRowKeys
  } = useTabState({tabKeys: tabKeysArr});

  const loadTotals = () => {
    getJournalEntriesTotals({
      successCallback: (data) => {
        setTotal({
          [NOT_ON_ERP]: data?.ready_for_review_count || 0,
          [READY_TO_SYNC]: data?.ready_for_submission_count || 0,
          [MISSING_BILLS]: data?.missing_data_count || 0
        });
      },
      errorCallback: () => setTotal(null)
    });
  }

  const loadData = ({
    view,
    query = null,
    initial = false,
    updateRequired = true,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    const finishTableLoading = () => {
      setLoading(prevState => ({...prevState, [view]: false}));
      if (updateRequired) {
        setRequiredUpdate(prevState => ({...prevState, [view]: false}));
      }
    }

    setLoading(prevState => ({...prevState, [view]: true}));

    getJournalEntries({
      query: getQuery(viewQuery, view),
      successCallback: (response) => {
        const expenseIds = response.results.map(i => i.expense_id).filter(Boolean);
        const invoiceIds = response.results.map(i => i.invoice_id).filter(Boolean);

        loadExpenses(expenseIds);
        loadInvoices(invoiceIds);

        tabHelpers.handleStoreTableDataWithPrev({
          tableData, setTableData, view, response, initial, updateCurrentPage
        });
        finishTableLoading();
        onSuccess?.();
      },
      errorCallback: finishTableLoading
    });
  }

  const {
    handleOnPaginate,
    handleOnTableChange,
    handleOnFilter,
    handleRowSelect,
    onChangeTab,
    updateTab,
  } = useAccountingTabLogic({
    tabKeys,
    tableData,
    setTableData,
    queries,
    setQueries,
    requiredUpdate,
    loadData,
    setActiveTabKey,
    setSelectedRowKeys
  });

  const handleOnTabChange = (key) => {
    onChangeTab(key);
    if (detailsWindowProps.open) setDetailsWindowProps(prevState => ({...prevState, open: false}));
  }

  const trans = (key, props) => t(`accounting:${key}`, props);

  const handleReadyToSync = (view = NOT_ON_ERP) => {
    const ids = selectedRowKeys?.[view] || [];

    if (!ids.length) {
      showAlert('warning', trans('validation.noItemsReadyToSync'));
      return;
    }

    setSyncButtonLoading(true);

    readyToSyncJournalEntries({
      ids,
      successCallback: ({ failed_ids = [], success_ids = [] }) => {
        if (failed_ids.length) {
          showAlert('error', trans('validation.failedToReadyToSyncJournalEntries', { count: failed_ids.length }));
        }

        if (success_ids.length) {
          setSelectedRowKeys(prevState => ({ ...prevState, [view]: [] }));
          loadData({
            view: NOT_ON_ERP,
            updateCurrentPage: true,
            onSuccess: () => {
              if (tableData[READY_TO_SYNC].loaded || !failed_ids.length) {
                loadData({ initial: true, view: READY_TO_SYNC });
              }
              if (!failed_ids.length) updateTab(READY_TO_SYNC);
              loadTotals();
            }
          })
        }
        setSyncButtonLoading(false);
      },
      errorCallback: (error) => {
        showAlert('error', error?.message || trans('validation.failedToReadyToSyncJournalEntries', { count: ids.length }));
        setSyncButtonLoading(false);
      }
    });
  };

  const syncEntries = ({ ids, onSuccess }) => {
    accountingHelpers.handleSyncItems({
      ids, loadData, onSuccess, onSync: syncJournalEntries, setSyncButtonLoading, trans
    });
  };

  const handleSync = () => {
    const ids = selectedRowKeys?.[READY_TO_SYNC] || [];
    syncEntries({
      ids,
      onSuccess: () => {
        setSelectedRowKeys(prevState => ({ ...prevState, [READY_TO_SYNC]: [] }));
        loadData({
          view: READY_TO_SYNC,
          updateCurrentPage: true,
          onSuccess: () => {
            if (tableData[READY_TO_SYNC].loaded) {
              loadData({ initial: true, view: READY_TO_SYNC });
            }
            loadTotals();
            updateTab(SYNCED);
          }
        });
      }
    });
  };

  const handleResync = () => {
    syncEntries({
      ids: selectedRowKeys?.[SYNCED] || [],
      onSuccess: () => setSelectedRowKeys(prevState => ({ ...prevState, [SYNCED]: [] }))
    });
  };

  const handleClickOnSyncButton = () => {
    const actions = {
      [MISSING_BILLS]: handleReadyToSync,
      [NOT_ON_ERP]: handleReadyToSync,
      [READY_TO_SYNC]: handleSync,
      [SYNCED]: handleResync,
    };

    actions[activeTabKey]?.(activeTabKey);
  }

  const formatJournalEntry = (entry) => {
    return accountingHelpers.formatJournalEntry({
      entry, expensesMap, invoicesMap, vendors
    })
  }

  const getTableFiltersRightSideContent = (view) => {
    if (view === MISSING_BILLS) return ;

    const availableSync = Boolean(selectedRowKeys?.[view].length);

    return (
      <TableFiltersRightSideContent
        syncButtonProps={{ disabled: !availableSync, loading: syncButtonLoading, onClick: availableSync && handleClickOnSyncButton }}
        syncButtonText={t(activeTabKey === NOT_ON_ERP ? 'readyToSync' : 'sync')}
      />
    )
  }

  useEffect(() => {
    loadData({
      initial: true,
      view: tabKeys[activeTabKey.toUpperCase()]
    });
    loadTotals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getLabel = ({ key, totalKey }) =>
    tabHelpers.getLabelWithOptionalTotal({ key, total, totalKey, t: (key) => trans(`journalEntries.${key}`) });

  const getQuery = (query, view) => {
    let statusesEnum = {
      [NOT_ON_ERP]: journalEntryStatusesConstants.READY_FOR_REVIEW,
      [READY_TO_SYNC]: journalEntryStatusesConstants.READY_FOR_SUBMISSION,
      [SYNCED]: journalEntryStatusesConstants.SUBMITTED,
      [MISSING_BILLS]: journalEntryStatusesConstants.MISSING_DATA,
    };
    return {
      status: statusesEnum[view],
      order_by: '-transaction_date',
      ...query,
    }
  }

  const loadExpenses = (ids) =>
    loadHelpers.loadItems({ids, fetchFn: getBatchExpenses, setMap: setExpensesMap, key: 'expenses_ids'});

  const loadInvoices = (ids) =>
    loadHelpers.loadItems({ids, fetchFn: getBatchInvoices, setMap: setInvoicesMap, key: 'invoices_ids'});

  const handleRowUpdate = ({id, data, view, sendRequest = true}) => {
    if (sendRequest) {
      updateJournalEntry({
        id,
        data,
        successCallback: (response) => {
          const updatedData = formatJournalEntry(response);
          const updatedRows = tableData[view].rows.map(row => row.id === id ? updatedData : row);
          setTableData(prevState => ({
            ...prevState,
            [view]: {
              ...tableData[view],
              rows: updatedRows
            }
          }));
        },
        errorCallback: () => {
          accountingHelpers.showAlert('error', trans('validation.failedToUpdateJournalEntry'));
        }
      })
    } else {
      const updatedRows = tableData[view].rows.map(row => row.id === id ? {...row, ...data} : row);
      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...tableData[view],
          rows: updatedRows
        }
      }));
    }
  }

  const handleCloseDetails = () => {
    setDetailsWindowProps(prevState => ({ ...prevState, open: false }));
  }

  const handleOpenDetails = (item, variant) => {
    setDetailsWindowProps(prevState => ({ ...prevState, open: true, item, variant }));
  }

  const handleMatchInvoiceSuccess = () => {
    const view = MISSING_BILLS;
    const item = detailsWindowProps.item;

    showAlert('success', trans('validation.journalEntryMatchingInProgress'));

    setDetailsWindowProps(prevState => ({ ...prevState, item: null, open: false }));

    // clear not on erp tab data and remove item from missing bills tab
    setTableData(prevState => ({
      ...prevState,
      [NOT_ON_ERP]: DEFAULT_TABLE_DATA,
      [MISSING_BILLS]: {
        ...prevState[MISSING_BILLS],
        rows: prevState[MISSING_BILLS].rows.filter(row => row.id !== item.id)
      }
    }));

    // load not on erp and missing bills tab after 3 seconds to give time for the backend to process the matching
    setTimeout(() => {
      loadData({ view, updateCurrentPage: true });
      loadTotals();
    }, 3000);
  }

  const handleMatchVendorSuccess = () => {
    setDetailsWindowProps(prevState => ({ ...prevState, item: null, open: false }));
  }

  const handleUpdateBillSuccess = (invoice) => {
    const view = NOT_ON_ERP;
    const invoiceId = invoice?.id;
    const updatedRow = formatJournalEntry({
      ...detailsWindowProps.item,
      vendor_bill_id: invoiceId,
      vendor_bill: { id: invoiceId, remote_id: invoice?.remote_id }
    });
    setTableData(prevState => ({
      ...tableData,
      [view]: {
        ...prevState[view],
        rows: prevState[view].rows.map(row => row.id === detailsWindowProps.item.id ? updatedRow : row)
      }
    }));
    setInvoicesMap(prevState => ({
      ...prevState,
      [invoiceId]: invoice
    }));
    setDetailsWindowProps(prevState => ({ ...prevState, item: null, open: false }));
  }

  const handleSuccessDetails = (props) => {
    const actions = {
      [accountingEditVariants.INVOICE_MATCHING]: handleMatchInvoiceSuccess,
      [accountingEditVariants.VENDOR_MISSING]: handleMatchVendorSuccess,
      [accountingEditVariants.BILL_MISSING]: handleUpdateBillSuccess,
    };
    actions[detailsWindowProps.variant]?.(props);
  }

  const handleClickOnNotOnERP = (e, record) => {
    if (elementHelpers.isInputOrButtonElement(e)) {
      e.stopPropagation();
      return;
    }

    if (!invoicesHelpers.isExpenseInvoiceType(record?.expense)) return;

    if (record?.vendor_id && !vendorsHelpers.isVendorAccountingReference(record.vendor)) {
      handleOpenDetails(record, accountingEditVariants.VENDOR_MISSING);
    } else if (accountingHelpers.isMissingInvoice(record)) {
      handleOpenDetails(record, accountingEditVariants.BILL_MISSING);
    }
  }

  const handleOnRowClick = (e, record) => {
    const actions = {
      [MISSING_BILLS]: () => handleOpenDetails(record, accountingEditVariants.INVOICE_MATCHING),
      [NOT_ON_ERP]: () => handleClickOnNotOnERP(e, record),
    };

    actions[activeTabKey]?.();
  }

  const handleRefresh = () => loadData({ initial: true, view: tabKeys[activeTabKey.toUpperCase()] });

  const handleAttachmentRemove = (e, id) => {
    elementHelpers.showConfirmModal({
      content: trans('modal.removeAttachment.description'),
      icon: <ExclamationCircleFilled />,
      okType: 'danger',
      onOk: () => {
        resetJournalEntry({
          id,
          successCallback: () => {
            loadData({
              view: tabKeys[activeTabKey.toUpperCase()],
              updateCurrentPage: true,
              onSuccess: () => {
                if (tableData[MISSING_BILLS].loaded) {
                  loadData({ view: MISSING_BILLS, initial: true });
                }
                showAlert('success', trans('validation.attachmentRemoved'));
              }
            });
            loadTotals();
          },
        });
      },
      title: trans('modal.removeAttachment.title'),
      translate: t,
    });
  }

  const getTabProps = (key) => {
    let tableProps = {
      onRow: (record) => ({
        onClick: (e) => handleOnRowClick(e, record)
      })
    };
    let tableFiltersProps = {};

    if (key === SYNCED) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          syncStatus: journalEntrySyncStatusConstants.ERROR,
          selectedRowKeys: selectedRowKeys[key] || [],
          onChange: (rows) => handleRowSelect(key, rows),
        })
      }
      tableFiltersProps = { leftSideContent: <ReloadButton onClick={handleRefresh} /> };
    } else if ([NOT_ON_ERP, READY_TO_SYNC].includes(key)) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          onChange: (rows) => handleRowSelect(key, rows),
          selectedRowKeys: selectedRowKeys[key],
        }),
      }
    }

    return accountingHelpers.getJournalEntriesTabProps({
      key,
      totalKey: key,
      tableData,
      expensesMap,
      invoicesMap,
      loading,
      handleOnFilter,
      handleRowUpdate,
      handleOnTableChange,
      handleOnPaginate,
      getTableFiltersRightSideContent,
      tableFiltersProps,
      tableProps,
      total,
      vendors,
      ...(key === NOT_ON_ERP ? { onAttachmentRemove: handleAttachmentRemove } : {})
    });
  }

  const tabs = [
    {
      key: MISSING_BILLS,
      label: getLabel({ key: MISSING_BILLS, totalKey: MISSING_BILLS }),
      children: <MissingBillsTab {...getTabProps(MISSING_BILLS)} />
    },
    {
      key: NOT_ON_ERP,
      label: getLabel({ key: NOT_ON_ERP, totalKey: NOT_ON_ERP }),
      children: <JournalEntryReviewTab {...getTabProps(NOT_ON_ERP)} />
    },
    {
      key: READY_TO_SYNC,
      label: getLabel({ key: READY_TO_SYNC, totalKey: READY_TO_SYNC }),
      children: <JournalEntryReviewTab {...getTabProps(READY_TO_SYNC)} />
    },
    {
      key: SYNCED,
      label: getLabel({ key: SYNCED }),
      children: <JournalEntryHistoryTab {...getTabProps(SYNCED)} />
    },
  ];

  return (
    <StyledJournalEntriesTabsContainer>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <StyledJournalEntriesTabs
            {...rest}
            activeKey={activeTabKey}
            items={tabs}
            onChange={handleOnTabChange}
            type='card'
            style={{ ...tableStyles.default, ...tableStyles[state] }}
          />
        )}
      </Transition>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <EditAccountingRowSection
            {...detailsWindowProps}
            className='accounting-details-panel'
            contentClassName='accounting-details-content'
            onBack={handleCloseDetails}
            onSuccess={handleSuccessDetails}
            style={{ ...detailsStyles.default, ...detailsStyles[state] }}
          />
        )}
      </Transition>
    </StyledJournalEntriesTabsContainer>
  );
}

const mapStateToProps = ({
  vendor: { vendors }
}) => ({
  vendors
});

const mapDispatchToProps = {
  getBatchExpenses: expenseActions.getBatchExpenses,
  getBatchInvoices: invoicesActions.getBatchInvoices,
  getJournalEntries: accountingActions.getJournalEntries,
  getJournalEntriesTotals: accountingActions.getJournalEntriesTotals,
  readyToSyncJournalEntries: accountingActions.readyToSyncJournalEntries,
  resetJournalEntry: accountingActions.resetJournalEntry,
  syncJournalEntries: accountingActions.syncJournalEntries,
  updateJournalEntry: accountingActions.updateJournalEntry,
}

export default connect(mapStateToProps, mapDispatchToProps)(JournalEntriesTabs);
